import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Generic polling hook for the live-session endpoints.
 *
 * - Calls `fetcher({ knownVersion, signal })` every `intervalMs` while the
 *   tab is visible and `enabled` is true.
 * - If the response has `unchanged: true`, the state is NOT updated (the
 *   server told us nothing changed). Any other response replaces `data`.
 * - Cancels the in-flight request when a new tick starts, the tab is hidden,
 *   the component unmounts, or `enabled` flips to false.
 *
 * Returns `{ data, error, refetch }`. `refetch()` forces an immediate poll.
 */
export function useLivePoll(fetcher, intervalMs, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const dataRef = useRef(null);
  const abortRef = useRef(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const tick = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const result = await fetcherRef.current({
        knownVersion: dataRef.current ? dataRef.current.version : undefined,
        signal: ctrl.signal
      });
      if (ctrl.signal.aborted) return;
      if (result && result.unchanged) {
        setError(null);
        return;
      }
      dataRef.current = result;
      setData(result);
      setError(null);
    } catch (e) {
      if (e && (e.name === 'CanceledError' || e.name === 'AbortError' || e.code === 'ERR_CANCELED')) return;
      setError(e);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    tick();
    const id = setInterval(tick, intervalMs);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [enabled, intervalMs, tick]);

  return { data, error, refetch: tick };
}
